import React, { useState } from "react";
import { Link2, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import CommitDateInput, { fmtDMY } from "./CommitDateInput";

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromIso = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) : undefined);

/** One-line date cell. Press the date text and the calendar pops up — no icon, no padlock. A date SET at
 *  this level reads bold (the system holds it there); an inherited date reads plain grey. The popover
 *  carries the calendar, a typed input and one small "back to inherited" link. */
export default function FrsDateCell({ value, onCommit, locked = false, canUnlock = true, readOnly = false, multi = false,
                                      past = false, hint = "", unlockLabel = "Back to inherited", linked = false,
                                      paused = false, forceOpen = false, extras = null, testId, className = "", plain = false }) {
  const [open, setOpen] = useState(false);
  const isOpen = open || forceOpen;
  const tone = readOnly ? "border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
    : locked ? "border-gray-400 bg-white text-gray-900 font-semibold dark:border-gray-500 dark:bg-gray-900 dark:text-gray-100"
    : past ? "border-amber-200 bg-white text-amber-700 dark:border-amber-700 dark:bg-gray-900 dark:text-amber-300"
    : "border-gray-200 bg-white text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300";
  const pick = (d) => { if (!d) return; onCommit(toIso(d)); setOpen(false); };
  return (
    <Popover open={isOpen} onOpenChange={(o) => setOpen(o)}>
      <span className={`inline-flex items-center gap-0.5 align-middle ${className}`} data-testid={testId} data-locked={locked ? "1" : "0"}>
        <PopoverTrigger asChild>
          {plain ? (
            <button type="button" title={hint} data-testid={`${testId}-open`}
                    className={`inline-flex items-center gap-1 whitespace-nowrap text-[11px] tabular-nums hover:text-[#007BFF] hover:underline ${
                      past ? "text-amber-700" : value ? (locked ? "font-semibold text-gray-900" : "text-gray-600") : "text-gray-400"}`}>
              {value ? <span data-testid={`${testId}-text`}>{fmtDMY(value)}</span> : multi ? "multi" : "Set…"}
            </button>
          ) : (
          <button type="button" className={`h-6 w-[78px] rounded border px-1 text-left text-[11px] tabular-nums ${tone} ${value ? "" : "text-gray-400"}`}
                  title={hint} data-testid={`${testId}-open`}>
            {fmtDMY(value) || (multi ? "multi" : "mm/dd/yy")}
          </button>
          )}
        </PopoverTrigger>
        {linked && (
          <Link2 className={`w-2.5 h-2.5 ${paused ? "text-gray-400" : "text-emerald-600"}`} data-testid={`${testId}-mark-link`}
                 title={paused ? "Linked — paused while the partner is complete" : "Linked scope — one start date"} />
        )}
      </span>
      <PopoverContent align="start" className="w-auto p-2 space-y-1.5 text-[11px]" data-testid={`${testId}-popover`}>
        <div className="flex items-center gap-2">
          <span className={`inline-flex max-w-[280px] ${locked ? "font-semibold text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}`}>
            {hint || (locked ? "Set here" : "Inherited")}
          </span>
          {locked && canUnlock && !readOnly && (
            <button type="button" onClick={() => { onCommit(""); setOpen(false); }}
                    className="ml-auto inline-flex items-center gap-0.5 text-gray-500 hover:text-gray-900 hover:underline"
                    data-testid={`${testId}-unlock`}><X className="w-3 h-3" /> {unlockLabel}</button>
          )}
        </div>
        {!readOnly && (
          <>
            <CommitDateInput value={value || ""} onCommit={(v) => { onCommit(v); setOpen(false); }} data-testid={`${testId}-input`}
                             wrapClassName="block" typedOnly
                             className="h-7 w-full text-[11px] border border-input rounded px-1.5 bg-background text-foreground placeholder:text-muted-foreground" />
            <Calendar mode="single" selected={fromIso(value)} defaultMonth={fromIso(value) || new Date()} onSelect={pick} className="p-1" data-testid={`${testId}-calendar`} />
          </>
        )}
        {extras && <div className="flex flex-wrap items-center gap-1 border-t border-gray-100 pt-1.5" data-testid={`${testId}-extras`}>{extras}</div>}
      </PopoverContent>
    </Popover>
  );
}
